import styled, { css } from 'styled-components'

import {
  _S_Normal,
  FloatingContainer as FloatingContainerComponent,
  mainColors,
  medias,
  SelectField as Select,
  TextFieldInputContainer,
  zIndex,
} from '@avantstay/backoffice-core'
import { IconChevronDown } from '@avantstay/backoffice-vectors'

import RadioField from '@new/__ui/RadioField'

export const Root = styled.div<{ $isSidebarOpen?: boolean; background?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: ${({ background }) => background ?? mainColors.neutralExtraLight};
  overflow: auto;
  padding: 10px 16px;
  z-index: ${zIndex.FILTERS};
  flex-shrink: 0;
  gap: 10px;
  flex-wrap: nowrap;

  ${({ $isSidebarOpen }) => medias.LG_SCREEN`
    padding: ${$isSidebarOpen ? '16px 10px 16px 0' : '16px 30px 16px 30px'};
  `}

  ${medias.LG_SCREEN`
    gap: 30px;
  `}

  ${medias.XL_MEDIUM_SCREEN`
    align-items: center;
  `}
`

export const Filters = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  background: inherit;

  ${medias.LG_SCREEN`
    min-width: fit-content;
  `}
`

export const RightSide = styled.div<{ $isSticky?: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: max-content;
  background: inherit;
  white-space: nowrap;
  flex-grow: 1;

  // Opt-in: pinning this side keeps the tape chart actions reachable while the filter row scrolls
  // sideways on a tablet. It also makes the row a scroll container for anything positioned inside
  // it, which grew the Pricing Tape Chart's filter bar by the height of its Modify menu, so the
  // screens that do not need pinning must not pay for it.
  ${({ $isSticky }) =>
    $isSticky &&
    css`
      flex-shrink: 0;
      position: sticky;
      right: 0;
      z-index: ${zIndex.FILTERS};
    `}

  ${medias.LG_SCREEN`
    height: 100%;
  `}
`

export const RadioFieldGroupStyled = styled(RadioField.Group)`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

export const Row = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  background: inherit;

  ${medias.MD_SCREEN`
    gap: 10px;
  `}
`

export const AppliedFiltersRow = styled(Row)`
  flex-wrap: wrap;
  gap: 10px;

  &:empty {
    // avoids generating gap when AppliedFiltersRow is empty
    display: none;
  }
`

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  button {
    padding-right: 30px;
  }

  ${medias.LG_SCREEN`
    width: max-content;
  `}
`

export const ButtonContainer = styled.div``

export const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-flow: row;
  gap: 10px;

  ${medias.MD_SCREEN`
    gap: 6px;
  `}

  ${medias.LG_SCREEN`
    gap: 10px;
  `}
`

export const FilterItem = styled.div<{ width?: number }>`
  &:first-child {
    width: 100%;
  }

  button {
    font-size: 13px !important;
    padding: 0 11px 0 14px;

    svg {
      color: ${mainColors.neutralExtraDark};
    }
  }

  ${({ width }) => width && `width: ${width}px`};

  ${medias.LG_SCREEN`
    &:first-child {
      width: auto;
    }
  `}
`

export const FilterInternalItem = styled.div`
  width: 100%;
`

export const FilterInternalWrapper = styled.div`
  margin-bottom: 14px;
`

export const FilterItemWrapper = styled.div`
  width: max-content;
  padding: 7px 14px 6px;
  background-color: ${mainColors.white};
  border: 2px solid ${mainColors.neutralMedium};
`

export const RangeInputWrapper = styled.div<{ flexDirection?: string }>`
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  flex-direction: ${({ flexDirection }) => flexDirection};
`

export const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`

export const SetAsAverage = styled.p`
  ${_S_Normal};
  color: ${mainColors.accentDark};
  cursor: pointer;
`

export const FilterFrame = styled.div<{ isSidebarFilter?: boolean }>`
  ${medias.LG_SCREEN`
    display: flex;
    align-items: center;
    background-color: ${mainColors.white};
    border: 2px solid ${mainColors.neutralMedium};
    border-radius: 0 2px 2px 0;
    height: 36px;
    min-width: max-content;
    padding: 0 14px;

    &:hover {
      color: ${mainColors.neutralExtremelyDark2};
      border: 2px solid ${mainColors.neutralExtraDark};
    }
  `}
`

export const SelectField = styled(Select.Small)<{ width?: number }>`
  min-width: ${({ width }) => width}px;
`

export const RadioFieldInlineContainer = styled.div`
  background-color: ${mainColors.white};
  padding: 9px 14px 9px 6px;
`

export const FloatingContainer = styled(FloatingContainerComponent)`
  min-width: 170px;
  margin-top: 10px;
  background: ${mainColors.white};
  padding: 10px 14px;
  border: 1px solid ${mainColors.neutralMedium};
  border-radius: 2px;
`

export const ReadOnlyInput = styled(TextFieldInputContainer)`
  ${_S_Normal};
  min-width: fit-content;
  justify-content: space-between;
  text-align: right;
  padding: 2px 10px;

  // input title

  span {
    max-width: max-content;
    color: ${mainColors.neutralExtremelyDark};
  }
`

export const DropdownIcon = styled(IconChevronDown)<{ $isActive?: boolean }>`
  transform: scale(${({ $isActive }) => $isActive && '-1'});
  width: 14px;
  height: 16px;
  max-width: 14px;
  color: ${mainColors.neutralExtraDark};
`

export const RadioExtraContentWrapper = styled.div`
  margin-top: 14px;
  width: 100%;
`
